/**
 * Authentication Store
 * Manages user authentication state and permissions
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useApi } from '@/composables/useApi';
import { USER_ROLES } from '@/utils/constants';

export const useAuthStore = defineStore('auth', () => {
  // State
  const currentUser = ref(null);
  const userPermissions = ref(null);
  const sectionActivations = ref({});
  const loading = ref(false);

  // Getters
  const isAuthenticated = computed(() => currentUser.value !== null);
  const isAdmin = computed(() => currentUser.value?.role === USER_ROLES.ADMIN);
  const userId = computed(() => currentUser.value?.id);
  const userFullName = computed(() => {
    if (!currentUser.value) return '';
    return `${currentUser.value.first_name} ${currentUser.value.last_name}`;
  });

  /**
   * Transform and merge permissions from backend format
   * @param {object} permissionsData - Permissions data from backend
   * @returns {array} - Flat array of permission keys
   */
  function transformPermissions(permissionsData) {
    if (!permissionsData) {
      return [];
    }

    const permissionMap = new Map();

    // Process group permissions first (lower priority)
    if (permissionsData.groups && Array.isArray(permissionsData.groups)) {
      permissionsData.groups.forEach(perm => {
        if (perm.has_access) {
          const key = perm.subsection
            ? `${perm.section}.${perm.subsection}`
            : perm.section;
          permissionMap.set(key, true);
        }
      });
    }

    // Process user permissions (higher priority - can override group)
    if (permissionsData.user && Array.isArray(permissionsData.user)) {
      permissionsData.user.forEach(perm => {
        const key = perm.subsection
          ? `${perm.section}.${perm.subsection}`
          : perm.section;

        if (perm.has_access) {
          permissionMap.set(key, true);
        } else {
          // User explicitly denied - remove even if granted by group
          permissionMap.delete(key);
        }
      });
    }

    // Convert to array
    const permissions = Array.from(permissionMap.keys());
    return permissions;
  }

  /**
   * Check if user has permission for a section
   * @param {string} section - Section key (e.g., 'company', 'team')
   * @param {string} subsection - Optional subsection key
   * @returns {boolean} - True if user has permission
   */
  function hasPermission(section, subsection = null) {
    // Admins always have access to everything
    if (isAdmin.value) {
      return true;
    }

    if (!userPermissions.value) {
      return false;
    }

    // Check for section-level permission first (grants access to all subsections)
    if (subsection && userPermissions.value.includes(section)) {
      return true;
    }

    // Check for specific subsection permission
    const key = subsection ? `${section}.${subsection}` : section;
    const hasAccess = userPermissions.value.includes(key);
    return hasAccess;
  }

  /**
   * Check if user has any permission for a section (section-level OR any subsection)
   * Used for sidebar visibility where having any subsection access should show the section
   * @param {string} section - Section key
   * @returns {boolean} - True if user has any permission in this section
   */
  function hasAnyPermissionInSection(section) {
    // Admins always have access
    if (isAdmin.value) {
      return true;
    }

    if (!userPermissions.value) {
      return false;
    }

    // Check for section-level permission
    if (userPermissions.value.includes(section)) {
      return true;
    }

    // Check for any subsection permission
    return userPermissions.value.some(perm => perm.startsWith(`${section}.`));
  }

  /**
   * Check if a section/subsection is activated (production-ready)
   * @param {string} section - Section key (e.g., 'company', 'team')
   * @param {string} subsection - Optional subsection key
   * @returns {boolean} - True if section is activated
   */
  function isSectionActive(section, subsection = null) {
    const key = subsection ? `${section}-${subsection}` : section;
    return sectionActivations.value[key] === true;
  }

  /**
   * Check authentication status
   * Fetches current user from server
   * @param {boolean} silent - If true, don't show global loading state (used in navigation guards)
   */
  async function checkAuth(silent = false) {
    const api = useApi(); // Create fresh API instance per method call
    if (!silent) {
      loading.value = true;
    }
    try {
      const data = await api.get('/auth/status');
      if (data.authenticated) {
        currentUser.value = data.user;
        userPermissions.value = transformPermissions(data.permissions);
        sectionActivations.value = data.sectionActivations || {};
        return true;
      } else {
        currentUser.value = null;
        userPermissions.value = null;
        sectionActivations.value = {};
        return false;
      }
    } catch (error) {
      currentUser.value = null;
      userPermissions.value = null;
      sectionActivations.value = {};
      return false;
    } finally {
      if (!silent) {
        loading.value = false;
      }
    }
  }

  /**
   * Login user
   * @param {object} credentials - Login credentials
   * @param {string} credentials.email - User email
   * @param {string} credentials.password - User password
   */
  async function login(credentials) {
    const api = useApi(); // Create fresh API instance per method call
    loading.value = true;
    try {
      const data = await api.post('/api/auth/login', credentials);
      currentUser.value = data.user;
      userPermissions.value = transformPermissions(data.permissions);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      loading.value = false;
    }
  }

  /**
   * Logout user
   */
  async function logout() {
    const api = useApi(); // Create fresh API instance per method call
    loading.value = true;
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear all state
      currentUser.value = null;
      userPermissions.value = null;
      loading.value = false;

      // Clear any cached data in sessionStorage/localStorage
      sessionStorage.clear();
      localStorage.removeItem('lastAuthCheck');
    }
  }

  /**
   * Reset store state
   */
  function $reset() {
    currentUser.value = null;
    userPermissions.value = null;
    sectionActivations.value = {};
    loading.value = false;
  }

  return {
    // State
    currentUser,
    userPermissions,
    sectionActivations,
    loading,

    // Getters
    isAuthenticated,
    isAdmin,
    userId,
    userFullName,

    // Actions
    hasPermission,
    hasAnyPermissionInSection,
    isSectionActive,
    checkAuth,
    login,
    logout,
    $reset,
  };
});
